import React from 'react';
import { motion } from 'framer-motion';

interface SkeletonLoaderProps {
  variant?: 'text' | 'circular' | 'rectangular' | 'card';
  width?: string | number;
  height?: string | number;
  className?: string;
  lines?: number; // For text variant
  animate?: boolean;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant = 'rectangular',
  width,
  height,
  className = '',
  lines = 1,
  animate = true
}) => {
  const baseClasses = 'bg-gray-200 rounded';
  
  const variants = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
    card: 'rounded-xl'
  };

  const animationProps = animate ? {
    animate: {
      opacity: [0.5, 1, 0.5],
    },
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  } : {};

  const style = {
    width: width || (variant === 'text' ? '100%' : '100px'),
    height: height || (variant === 'text' ? '1rem' : variant === 'circular' ? '40px' : '100px')
  };

  if (variant === 'text' && lines > 1) {
    return (
      <div className={`space-y-2 ${className}`}>
        {Array.from({ length: lines }).map((_, index) => (
          <motion.div
            key={index}
            className={`${baseClasses} ${variants[variant]}`}
            style={{
              ...style,
              width: index === lines - 1 ? '75%' : '100%'
            }}
            {...animationProps}
          />
        ))}
      </div>
    );
  }

  return (
    <motion.div
      className={`${baseClasses} ${variants[variant]} ${className}`}
      style={style}
      {...animationProps}
    />
  );
};

// Pre-built skeleton components for common use cases
export const DocumentCardSkeleton: React.FC = () => (
  <div className="p-6 border border-gray-200 rounded-xl space-y-4">
    <div className="flex items-center justify-between">
      <SkeletonLoader variant="circular" width={24} height={24} />
      <SkeletonLoader variant="circular" width={24} height={24} />
    </div>
    
    <div className="flex flex-col items-center space-y-3">
      <SkeletonLoader variant="rectangular" width={64} height={64} />
      <div className="flex gap-2">
        <SkeletonLoader variant="rectangular" width={60} height={20} />
        <SkeletonLoader variant="rectangular" width={60} height={20} />
      </div>
    </div>
    
    <div className="space-y-3">
      <SkeletonLoader variant="text" width="100%" />
      <SkeletonLoader variant="text" width="60%" />
      <SkeletonLoader variant="text" width="80%" />
    </div>
    
    <div className="pt-3 border-t border-gray-100">
      <SkeletonLoader variant="rectangular" width="100%" height={36} />
    </div>
    
    <div className="flex gap-2">
      <SkeletonLoader variant="rectangular" width="100%" height={32} />
      <SkeletonLoader variant="rectangular" width="100%" height={32} />
      <SkeletonLoader variant="rectangular" width="100%" height={32} />
    </div>
  </div>
);

export const StatCardSkeleton: React.FC = () => (
  <div className="p-6 border border-gray-200 rounded-xl">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <SkeletonLoader variant="text" width={120} />
        <SkeletonLoader variant="text" width={60} height={32} />
      </div>
      <SkeletonLoader variant="rectangular" width={48} height={48} />
    </div>
  </div>
);

export const SidebarSkeleton: React.FC = () => (
  <div className="w-64 bg-white border-r border-gray-200 p-6 space-y-6">
    {/* Header */}
    <div className="flex items-center gap-3">
      <SkeletonLoader variant="rectangular" width={40} height={40} />
      <div className="space-y-1">
        <SkeletonLoader variant="text" width={120} />
        <SkeletonLoader variant="text" width={80} />
      </div>
    </div>
    
    {/* Quick Actions */}
    <div className="space-y-2">
      <SkeletonLoader variant="rectangular" width="100%" height={36} />
      <SkeletonLoader variant="rectangular" width="100%" height={36} />
    </div>
    
    {/* Navigation */}
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 p-2">
          <SkeletonLoader variant="circular" width={20} height={20} />
          <SkeletonLoader variant="text" width={100} />
        </div>
      ))}
    </div>
  </div>
);

export default SkeletonLoader;